import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { scroller } from 'react-scroll';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';

import alertState from '@/states/alertState';
import { voipOptionPriceState } from '@/states/voipOptionPriceState';

import VoIPServicePackInfoTable from '@/components/organisms/VoIPServicePackInfoTable';

import useCallApiGetVoipOptionPrice from '@/hooks/api/useCallApiGetVoipOptionPrice';
import useCallAPIPutVoipOptionChange from '@/hooks/api/useCallAPIPutVoipOptionChange';

import { VoipOptionChangeState, VoipOptionChangeStateType } from '@/states/voipOptionChangeState';

import {
  containCatchPhone,
  containNumberDisplay,
  containNumberRequest,
  containStopNuisanceCall,
  containVoiceWarp,
} from '@/utils/common';

import { getOptionName, getPrice } from '@/pages/option/voip/index';

import {
  createPutVoipOptionChangeRequest,
  getServicePackServiceStartClassTypeCode,
  getVoipOption,
  isIncludeVoipOptionServicePack,
} from '@/logics/option/voip/index';

import {
  VoipChangeNumberNoticeAfterLabel,
  VoipChangeNumberNoticeBeforeLabel,
  VoipChangeOptionAfterLabel,
  VoipChangeOptionBeforeLabel,
} from '@/components/molecules/options/voip/VoipChange';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import historyContext from '@/hooks/historyContext';
import { VoipOptionPriceResponse } from '@/types/api/mypage/voipoptionprice';
import { Constants } from '@/utils/constants';
import { VoipOptionUseStatus } from '@/utils/constants/voip';

/**
 * 「番号リクエスト」を入力可能にするかどうかを返す。
 * 番号表示サービスやサービスパックとの依存関係があるため、単純なチェックボックス状態だけでは判断できない。
 */
const OptionNumberRequestDisabled = (
  inputNumberDisplay: boolean,
  servicePackType: string,
  voipOptionPrice: VoipOptionPriceResponse
): boolean => {
  const isIncludeServicePack = isIncludeVoipOptionServicePack(
    Constants.VoipOptionClassType.VOIP_NUMBER_INFO,
    servicePackType
  );
  if (isIncludeServicePack) {
    return true;
  }
  /**
   * 「番号表示サービス」の契約状態により戻り値を変更します。
   * 「契約ステータス == 利用中」は「番号表示サービス = 入力あり = 解約」になるため非活性にする。
   * 「契約スタータス != 利用中」は「番号表示サービス = 入力あり = 申込」になるため活性にする。
   */
  const optionNumberDisplay = getVoipOption(
    Constants.VoipOptionClassType.VOIP_NUMBER_DISPLAY,
    voipOptionPrice
  );
  if (optionNumberDisplay?.use_status === VoipOptionUseStatus.SERVICE) {
    if (inputNumberDisplay) {
      return true;
    }
    return false;
  }
  if (inputNumberDisplay) {
    return false;
  }
  return true;
};

/**
 * 「VoIPオプション変更」の入力画面
 * @returns 画面
 */
const VoIPChange: NextPage = () => {
  // 履歴コンテキスト情報のフック
  const history = useContext(historyContext);

  // 「VoIPオプション変更情報」の状態
  const [changeVoipOptionInfo, setChangeVoipOptionInfo] =
    useRecoilState<VoipOptionChangeStateType>(VoipOptionChangeState);

  // 「VoIPオプション変更情報」の状態リセット
  const resetVoipOptionChangeState = useResetRecoilState(VoipOptionChangeState);

  const setAlert = useSetRecoilState(alertState);

  const voipOptionPrice = useRecoilValue(voipOptionPriceState);

  const [localErrorMessage, setLocalErrorMessage] = useState<string>('');
  const [validateOptionsErrorMessage, setValidateOptionsErrorMessage] = useState<string>('');

  const { execute: callApiGetVoipOptionPrice } = useCallApiGetVoipOptionPrice();
  const { execute: callAPIPutVoipOptionChange } = useCallAPIPutVoipOptionChange();

  const router = useRouter();

  // 「VoIPオプション」に関連するAPI結果による初期化処理のマウント処理実行有無を保持する。
  const isVoipOptionPriceEffectMounted = useRef(false);

  const [initializeVoipOptionServicePack, setInitializeVoipOptionServicePack] =
    useState<string>('');

  // 現在契約中の「サービスパック」の情報を取得する。
  const beforeOptionServicePack: string = useMemo(() => {
    return initializeVoipOptionServicePack;
  }, [initializeVoipOptionServicePack]);

  /**
   * 初期化処理を実施する。
   */
  useEffect((): void => {
    // 確認画面から戻った時だけ入力内容を保持し、それ以外の流入では前回状態を破棄する。
    // 遷移元が「確認画面」以外の場合は、入力情報を初期化する。
    const previousUrl = history[0];
    if (previousUrl !== '/option/voip/change/confirm/') {
      resetVoipOptionChangeState();
    }
    callApiGetVoipOptionPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 「VoIPオプション」に関連するAPI結果による初期化処理を実施する。
   */
  useEffect((): any => {
    // マウント済みの場合のみ処理を実行する。
    if (!isVoipOptionPriceEffectMounted.current) {
      return () => {
        isVoipOptionPriceEffectMounted.current = true;
      };
    }
    const servicePack = getServicePackServiceStartClassTypeCode(voipOptionPrice);
    setInitializeVoipOptionServicePack(servicePack);
    // 初期の契約パックは API から補完するが、確認画面から戻った時の入力値は上書きしない。
    /**
     * 「サービスパック」の入力情報が存在している場合は設定しない。
     * 「確認画面 -> 入力画面」に遷移するときに初期化されてしまうため。
     */
    if (changeVoipOptionInfo.optionServicePack) {
      return () => {};
    }
    setChangeVoipOptionInfo({
      ...changeVoipOptionInfo,
      optionServicePack: servicePack,
    });
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voipOptionPrice]);

  // パック・付加サービス選択時のエラーがあった場合はエラー表示部分にスクロール
  useEffect((): void => {
    if (validateOptionsErrorMessage !== '') {
      scroller.scrollTo('validateOptionsErrorMessage', {
        duration: 0,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: -500,
      });
    }
  }, [validateOptionsErrorMessage]);

  // 確認ボタンクリック
  const clickConfirm = (): void => {
    // 入力妥当性だけでなく、実際に変更差分があるかもここで確認して無駄な API 実行を避ける。
    // パック・オプション選択のエラー発生確認
    if (validateOptionsErrorMessage.length > 0) {
      setLocalErrorMessage('入力内容が正しくありません。');
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    // 変更有無チェック
    if (!validateChangeCulumn()) {
      setLocalErrorMessage('オプションの内容が変更されていません。');
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    const params = createPutVoipOptionChangeRequest(changeVoipOptionInfo, voipOptionPrice, '0');
    callAPIPutVoipOptionChange(params)
      .then(() => {
        setLocalErrorMessage('');
        // 確認画面に遷移
        router.push('/option/voip/change/confirm');
      })
      .catch((error) => {
        if (error instanceof TypeError) {
          // エラーダイアログ表示済みのため処理不要
          return;
        }
        // 当日の申込書請求が存在する／その他のエラー
        const { data } = error.response;
        switch (data.error_code) {
          case 'VALIDATION_ERROR': {
            // パラメータエラー
            setAlert({
              isOpen: true,
              apiType: Constants.ApiType.API_GET,
            });
            break;
          }
          case 'ALREADY_ACCEPTED': {
            // VoIPサービス変更受付中
            setLocalErrorMessage(Constants.VoipEnter.Message.CHANGE_DUPLICATE);
            window.scrollTo({ top: 0, behavior: 'auto' });
            break;
          }
          case 'UNACCEPTABLE_STATUS': // VoIPサービス変更受付不可ステータス
          case 'VOIP_UNUSED': {
            // VoIP未利用
            setLocalErrorMessage(Constants.VoipEnter.Message.CHANGE_NG);
            window.scrollTo({ top: 0, behavior: 'auto' });
            break;
          }
          default: {
            break;
          }
        }
      });
  };

  /**
   * サービスパックごとの選択オプションの変更
   * @param type '01', '02', '03', '04', '05'
   */
  const changeSelectOptions = (type: string) => {
    // サービスパック変更時は単品オプションとの包含関係が変わるため、一度選択状態を組み直す。
    setValidateOptionsErrorMessage('');
    setLocalErrorMessage('');

    // recoilの値反映にラグがあるためテンポラリ値を使用
    let { optionServicePack } = changeVoipOptionInfo;

    if (type === 'notChange') {
      // 変更なし選択時
      // サービスパック：変更前の状態に戻す
      optionServicePack = beforeOptionServicePack;
    } else {
      optionServicePack = type;
    }

    setChangeVoipOptionInfo((v) => {
      return {
        ...v,
        optionServicePack,
        optionNumberDisplay: false,
        optionNumberRequest: false,
        optionCatchPhone: false,
        optionStopNuisanceCall: false,
        optionVoiceWarp: false,
      };
    });

    // 更新結果を使用して処理を実行するようにする。
    let inputChange;
    setChangeVoipOptionInfo((v) => {
      inputChange = v;
      return v;
    });
    // 単品オプションの選択可否チェック
    validateOptions('', false, inputChange);
  };

  /**
   * 単品オプションの選択可否チェックとパック推奨メッセージ表示。
   * サービスパックと単品オプションには包含関係があり、全単品選択済みの場合は
   * よりお得なパックへの切り替えを推奨するメッセージを表示する。
   * @param type 変更対象のオプション種別
   * @param checked チェック状態
   * @param changeInput 現在の入力値（Recoilの非同期更新対策として引数で受け取る）
   */
  const validateOptions = (
    type: string,
    checked: boolean,
    changeInput: VoipOptionChangeStateType = changeVoipOptionInfo
  ) => {
    setValidateOptionsErrorMessage('');
    setLocalErrorMessage('');

    // recoilの値反映にラグがあるためテンポラリ値を使用
    let { optionNumberDisplay } = changeInput;
    let { optionNumberRequest } = changeInput;
    let { optionCatchPhone } = changeInput;
    let { optionStopNuisanceCall } = changeInput;
    let { optionVoiceWarp } = changeInput;

    // 単品付加サービスの選択状態を保持
    if (type === 'numberDisplay') {
      if (
        containNumberDisplay(beforeOptionServicePack) &&
        containNumberDisplay(changeInput.optionServicePack)
      ) {
        optionNumberDisplay = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionNumberDisplay,
        });
      } else if (!containNumberDisplay(changeInput.optionServicePack)) {
        if (!checked) {
          optionNumberDisplay = false;
          setChangeVoipOptionInfo({
            ...changeInput,
            optionNumberDisplay,
          });
        } else {
          optionNumberDisplay = checked;
          setChangeVoipOptionInfo({
            ...changeInput,
            optionNumberDisplay,
          });
        }
      }
    } else if (type === 'numberRequest') {
      if (
        !containNumberRequest(beforeOptionServicePack) &&
        !containNumberRequest(changeInput.optionServicePack)
      ) {
        optionNumberRequest = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionNumberRequest,
        });
      } else if (!containNumberRequest(changeInput.optionServicePack)) {
        optionNumberRequest = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionNumberRequest,
        });
      }
    } else if (type === 'catchPhone') {
      if (
        !containCatchPhone(beforeOptionServicePack) &&
        !containCatchPhone(changeInput.optionServicePack)
      ) {
        optionCatchPhone = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionCatchPhone,
        });
      } else if (!containCatchPhone(changeInput.optionServicePack)) {
        optionCatchPhone = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionCatchPhone,
        });
      }
    } else if (type === 'stopNuisanceCall') {
      if (
        !containStopNuisanceCall(beforeOptionServicePack) &&
        !containStopNuisanceCall(changeInput.optionServicePack)
      ) {
        optionStopNuisanceCall = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionStopNuisanceCall,
        });
      } else if (!containStopNuisanceCall(changeInput.optionServicePack)) {
        optionStopNuisanceCall = checked;
        setChangeVoipOptionInfo({
          ...changeInput,
          optionStopNuisanceCall,
        });
      }
    } else if (type === 'voiceWarp') {
      if (
        !containVoiceWarp(beforeOptionServicePack) &&
        !containVoiceWarp(changeInput.optionServicePack)
      ) {
        optionVoiceWarp = checked;
        setChangeVoipOptionInfo({
          ...changeVoipOptionInfo,
          optionVoiceWarp,
        });
      } else if (!containVoiceWarp(changeInput.optionServicePack)) {
        optionVoiceWarp = checked;
        setChangeVoipOptionInfo({
          ...changeVoipOptionInfo,
          optionVoiceWarp,
        });
      }
    }

    // --- パック推奨ロジック ---
    // 現在選択中のパックと単品オプションの組み合わせから、よりお得なパックを推奨する。
    // 例: パックなしで全オプション選択済み → サービスパック1を推奨
    if (changeInput.optionServicePack === '02') {
      // サービスパック２選択時
      if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall &&
        optionVoiceWarp
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK1); // サービスパック１を促す(P30W0104)
      }
    } else if (changeInput.optionServicePack === '03') {
      // サービスパック３選択時
      if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall &&
        optionVoiceWarp
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK1); // サービスパック１を促す(P30W0104)
      }
    } else if (changeInput.optionServicePack === '04') {
      // サービスパック４選択時
      if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall &&
        optionVoiceWarp
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK1); // サービスパック１を促す(P30W0104)
      } else if (optionCatchPhone && optionStopNuisanceCall && optionVoiceWarp) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK2); // サービスパック２を促す(P30W0105)
      } else if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK3); // サービスパック３を促す(P30W0106)
      }
    } else if (changeInput.optionServicePack === '05') {
      // ベーシックパック選択時
      if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall &&
        optionVoiceWarp
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK1); // サービスパック１を促す(P30W0104)
      } else if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK3); // サービスパック３を促す(P30W0106)
      }
    } else if (changeInput.optionServicePack === '00') {
      // サービスパックなし選択時
      if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall &&
        optionVoiceWarp
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK1); // サービスパック１を促す(P30W0104)
      } else if (optionCatchPhone && optionStopNuisanceCall && optionVoiceWarp) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK2); // サービスパック２を促す(P30W0105)
      } else if (
        optionNumberDisplay &&
        optionNumberRequest &&
        optionCatchPhone &&
        optionStopNuisanceCall
      ) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK3); // サービスパック３を促す(P30W0106)
      } else if (optionCatchPhone && optionStopNuisanceCall) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK4); // サービスパック４を促す(P30W0107)
      } else if (optionNumberDisplay && optionNumberRequest) {
        setValidateOptionsErrorMessage(Constants.VoipEnter.Message.PACK_BASIC); // ベーシックパックを促す(P30W0108)
      }
    }
  };

  /**
   * VoIPオプションの内容の変更有無をチェック。
   * パックまたは単品オプションのいずれか一つでも変更があればtrueを返す。
   * 変更なしのまま確認ボタンを押た場合のエラー表示に使用する。
   */
  const validateChangeCulumn = () => {
    if (beforeOptionServicePack !== changeVoipOptionInfo.optionServicePack) {
      return true;
    }
    if (changeVoipOptionInfo.optionNumberNotice) {
      return true;
    }
    if (changeVoipOptionInfo.optionNumberDisplay) {
      return true;
    }
    if (changeVoipOptionInfo.optionNumberRequest) {
      return true;
    }
    if (changeVoipOptionInfo.optionCatchPhone) {
      return true;
    }
    if (changeVoipOptionInfo.optionVoiceWarp) {
      return true;
    }
    if (changeVoipOptionInfo.optionStopNuisanceCall) {
      return true;
    }
    return false;
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>NURO 光 でんわ 付加サービス変更</Heading>
        <div className='m-box-02'>
          <div className='m-box-02__inner'>
            <div className='form-area' style={{ width: 'auto' }}>
              <p
                className='m-text'
                id='localErrorMessage'
                style={{ textAlign: 'center' }}
                data-testid='localErrorMessage'
                role={localErrorMessage ? 'alert' : undefined}
                aria-live={localErrorMessage ? 'assertive' : undefined}
              >
                <strong>{localErrorMessage}</strong>
              </p>
              <p>変更したい内容をご入力いただき、「確認する」ボタンをクリックしてください。</p>
              <p>
                <small>
                  ※お申し込み完了後、申込情報が反映されるまで時間がかかる場合がございます。
                </small>
              </p>

              {/* NURO 光 でんわ 付加サービス */}
              <Heading as='h2'>NURO 光 でんわ 付加サービス</Heading>
              <table>
                <colgroup>
                  <col width='30%' />
                  <col width='35%' />
                  <col width='35%' />
                </colgroup>
                <tbody>
                  <tr>
                    <th className='m-table__th'>基本サービス</th>
                    <th className='m-table__th'>変更前</th>
                    <th className='m-table__th'>変更後</th>
                  </tr>
                  <tr>
                    <th className='m-table__th'>発信者番号通知サービス（無料）</th>
                    <td className='m-table__td'>
                      <VoipChangeNumberNoticeBeforeLabel
                        voipOptionPrice={voipOptionPrice}
                        style={{ dataTestId: 'beforeOptionNumberNotice' }}
                      />
                    </td>
                    <td className='m-table__td'>
                      <label htmlFor='optionNumberNotice'>
                        <input
                          type='checkbox'
                          id='optionNumberNotice'
                          name='optionNumberNotice'
                          checked={changeVoipOptionInfo.optionNumberNotice}
                          onChange={(e): void => {
                            setChangeVoipOptionInfo({
                              ...changeVoipOptionInfo,
                              optionNumberNotice: e.target.checked,
                            });
                          }}
                          data-testid='optionNumberNotice'
                        />
                        <VoipChangeNumberNoticeAfterLabel
                          voipOptionPrice={voipOptionPrice}
                          style={{ dataTestId: 'optionNumberNoticeLabel' }}
                        />
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>

              <br />

              {/** 付加サービスパックの内容 */}
              {VoIPServicePackInfoTable({})}

              <br />

              {/* 付加サービス（パック） */}
              <Heading as='h2'>付加サービス（パック）</Heading>
              <table>
                <colgroup>
                  <col width='26%' />
                  <col width='48%' />
                  <col width='13%' />
                  <col width='13%' />
                </colgroup>
                <tbody>
                  <tr>
                    <th className='m-table__th'>変更前</th>
                    <th className='m-table__th'>変更後</th>
                    <th className='m-table__th'>工事費</th>
                    <th className='m-table__th'>月額費用</th>
                  </tr>
                  <tr>
                    <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                      <span data-testid='beforeServicePack'>
                        {beforeOptionServicePack === '00'
                          ? '未申込'
                          : getOptionName(voipOptionPrice.service_packs, beforeOptionServicePack)}
                      </span>
                    </td>
                    <td className='m-table__td'>
                      <input type='hidden' name='optionServicePack' ng-model='optionServicePack' />
                      <label htmlFor='sp_99'>
                        <input
                          type='radio'
                          id='sp_99'
                          name='optionServicePackView'
                          checked={
                            changeVoipOptionInfo.optionServicePack === beforeOptionServicePack
                          }
                          onChange={(): void => {
                            changeSelectOptions('notChange');
                          }}
                          value='notChange'
                          required
                          data-testid='sp_99'
                        />
                        <span data-testid='sp99Label'>変更しない</span>
                      </label>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='sp99Initial'>－</span>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='sp99Monthly'>
                        {getPrice(
                          voipOptionPrice.service_packs,
                          beforeOptionServicePack,
                          'monthlyPrice'
                        )}
                      </span>
                    </td>
                  </tr>

                  {beforeOptionServicePack !== '00' && (
                    <tr>
                      <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                        &nbsp;
                      </td>
                      <td className='m-table__td'>
                        <label htmlFor='sp_00'>
                          <input
                            type='radio'
                            id='sp_00'
                            name='optionServicePackView'
                            checked={changeVoipOptionInfo.optionServicePack === '00'}
                            onChange={(): void => {
                              changeSelectOptions('00');
                            }}
                            value='00'
                            required
                            data-testid='sp_00'
                          />
                          <span data-testid='sp00Label'>
                            {getOptionName(voipOptionPrice.service_packs, beforeOptionServicePack)}
                            を解約する
                          </span>
                        </label>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp00Initial'>－</span>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp00Monthly'>－</span>
                      </td>
                    </tr>
                  )}

                  {beforeOptionServicePack !== '01' && (
                    <tr>
                      <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                        &nbsp;
                      </td>
                      <td className='m-table__td'>
                        <label htmlFor='sp_01'>
                          <input
                            type='radio'
                            id='sp_01'
                            name='optionServicePackView'
                            checked={changeVoipOptionInfo.optionServicePack === '01'}
                            onChange={(): void => {
                              changeSelectOptions('01');
                            }}
                            value='01'
                            required
                            data-testid='sp_01'
                          />
                          <span data-testid='sp01Label'>
                            {getOptionName(voipOptionPrice.service_packs, '01')}に変更する
                          </span>
                        </label>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp01Initial'>
                          {getPrice(voipOptionPrice.service_packs, '01', 'initialPrice')}
                        </span>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp01Monthly'>
                          {getPrice(voipOptionPrice.service_packs, '01', 'monthlyPrice')}
                        </span>
                      </td>
                    </tr>
                  )}

                  {beforeOptionServicePack !== '02' && (
                    <tr>
                      <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                        &nbsp;
                      </td>
                      <td className='m-table__td'>
                        <label htmlFor='sp_02'>
                          <input
                            type='radio'
                            id='sp_02'
                            name='optionServicePackView'
                            checked={changeVoipOptionInfo.optionServicePack === '02'}
                            onChange={(): void => {
                              changeSelectOptions('02');
                            }}
                            value='02'
                            required
                            data-testid='sp_02'
                          />
                          <span data-testid='sp02Label'>
                            {getOptionName(voipOptionPrice.service_packs, '02')}に変更する
                          </span>
                        </label>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp02Initial'>
                          {getPrice(voipOptionPrice.service_packs, '02', 'initialPrice')}
                        </span>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp02Monthly'>
                          {getPrice(voipOptionPrice.service_packs, '02', 'monthlyPrice')}
                        </span>
                      </td>
                    </tr>
                  )}

                  {beforeOptionServicePack !== '03' && (
                    <tr>
                      <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                        &nbsp;
                      </td>
                      <td className='m-table__td'>
                        <label htmlFor='sp_03'>
                          <input
                            type='radio'
                            id='sp_03'
                            name='optionServicePackView'
                            checked={changeVoipOptionInfo.optionServicePack === '03'}
                            onChange={(): void => {
                              changeSelectOptions('03');
                            }}
                            value='03'
                            required
                            data-testid='sp_03'
                          />
                          <span data-testid='sp03Label'>
                            {getOptionName(voipOptionPrice.service_packs, '03')}に変更する
                          </span>
                        </label>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp03Initial'>
                          {getPrice(voipOptionPrice.service_packs, '03', 'initialPrice')}
                        </span>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp03Monthly'>
                          {getPrice(voipOptionPrice.service_packs, '03', 'monthlyPrice')}
                        </span>
                      </td>
                    </tr>
                  )}

                  {beforeOptionServicePack !== '04' && (
                    <tr>
                      <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                        &nbsp;
                      </td>
                      <td className='m-table__td'>
                        <label htmlFor='sp_04'>
                          <input
                            type='radio'
                            id='sp_04'
                            name='optionServicePackView'
                            checked={changeVoipOptionInfo.optionServicePack === '04'}
                            onChange={(): void => {
                              changeSelectOptions('04');
                            }}
                            value='04'
                            required
                            data-testid='sp_04'
                          />
                          <span data-testid='sp04Label'>
                            {getOptionName(voipOptionPrice.service_packs, '04')}に変更する
                          </span>
                        </label>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp04Initial'>
                          {getPrice(voipOptionPrice.service_packs, '04', 'initialPrice')}
                        </span>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp04Monthly'>
                          {getPrice(voipOptionPrice.service_packs, '04', 'monthlyPrice')}
                        </span>
                      </td>
                    </tr>
                  )}

                  {beforeOptionServicePack !== '05' && (
                    <tr>
                      <td className='m-table__td' style={{ verticalAlign: 'top' }}>
                        &nbsp;
                      </td>
                      <td className='m-table__td'>
                        <label htmlFor='sp_05'>
                          <input
                            type='radio'
                            id='sp_05'
                            name='optionServicePackView'
                            checked={changeVoipOptionInfo.optionServicePack === '05'}
                            onChange={(): void => {
                              changeSelectOptions('05');
                            }}
                            value='05'
                            required
                            data-testid='sp_05'
                          />
                          <span data-testid='sp05Label'>
                            {getOptionName(voipOptionPrice.service_packs, '05')}に変更する
                          </span>
                        </label>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp05Initial'>
                          {getPrice(voipOptionPrice.service_packs, '05', 'initialPrice')}
                        </span>
                      </td>
                      <td className='m-table__td amount'>
                        <span data-testid='sp05Monthly'>
                          {getPrice(voipOptionPrice.service_packs, '05', 'monthlyPrice')}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div>
                <small>
                  <ul>
                    <li>※特に注記のない限り、記載の金額は全て税込金額です。</li>
                    <li>※「－」：発生しない</li>
                  </ul>
                </small>
              </div>

              <br />

              {/* 付加サービス（単品） */}
              <Heading as='h2'>付加サービス（単品）</Heading>
              <table>
                <colgroup>
                  <col width='32%' />
                  <col width='21%' />
                  <col width='21%' />
                  <col width='13%' />
                  <col width='13%' />
                </colgroup>
                <tbody>
                  <tr>
                    <th className='m-table__th'>サービス名</th>
                    <th className='m-table__th'>変更前</th>
                    <th className='m-table__th'>変更後</th>
                    <th className='m-table__th'>工事費</th>
                    <th className='m-table__th'>月額費用</th>
                  </tr>
                  {/* 番号表示サービス */}
                  <tr>
                    <td className='m-table__td'>
                      <span data-testid='numberDisplayLabel'>番号表示サービス</span>
                    </td>
                    <td className='m-table__td'>
                      <VoipChangeOptionBeforeLabel
                        classType={Constants.VoipOptionClassType.VOIP_NUMBER_DISPLAY}
                        servicePackType={beforeOptionServicePack}
                        voipOptionPrice={voipOptionPrice}
                        style={{
                          dataTestId: 'numberDisplayLabelBefore',
                        }}
                      />
                    </td>
                    <td className='m-table__td'>
                      <input type='hidden' name='optionNumberDisplay' />
                      <label htmlFor='op_01'>
                        <input
                          type='checkbox'
                          id='op_01'
                          name='optionNumberDisplayView'
                          checked={changeVoipOptionInfo.optionNumberDisplay}
                          disabled={containNumberDisplay(changeVoipOptionInfo.optionServicePack)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            validateOptions('numberDisplay', e.target.checked);
                          }}
                          data-testid='optionNumberDisplayView'
                        />
                        <VoipChangeOptionAfterLabel
                          classType={Constants.VoipOptionClassType.VOIP_NUMBER_DISPLAY}
                          servicePackType={changeVoipOptionInfo.optionServicePack}
                          voipOptionPrice={voipOptionPrice}
                          style={{
                            dataTestId: 'numberDisplayLabelAfter',
                          }}
                        />
                      </label>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='numberDisplayLabelInitial'>
                        {getPrice(voipOptionPrice.voip_options, 'numberDisplay', 'initialPrice')}
                      </span>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='numberDisplayLabelMonthly'>
                        {getPrice(voipOptionPrice.voip_options, 'numberDisplay', 'monthlyPrice')}
                      </span>
                    </td>
                  </tr>
                  {/* 番号通知リクエストサービス */}
                  <tr>
                    <td className='m-table__td'>
                      <span data-testid='numberRequestLabel'>番号通知リクエストサービス</span>
                    </td>
                    <td className='m-table__td'>
                      <VoipChangeOptionBeforeLabel
                        classType={Constants.VoipOptionClassType.VOIP_NUMBER_INFO}
                        servicePackType={beforeOptionServicePack}
                        voipOptionPrice={voipOptionPrice}
                        style={{
                          dataTestId: 'numberRequestLabelBefore',
                        }}
                      />
                    </td>
                    <td className='m-table__td'>
                      <input
                        type='hidden'
                        name='optionNumberRequest'
                        ng-model='optionNumberRequest'
                      />
                      <label htmlFor='op_02'>
                        <input
                          type='checkbox'
                          id='op_02'
                          name='optionNumberRequestView'
                          checked={changeVoipOptionInfo.optionNumberRequest}
                          disabled={OptionNumberRequestDisabled(
                            changeVoipOptionInfo.optionNumberDisplay,
                            changeVoipOptionInfo.optionServicePack,
                            voipOptionPrice
                          )}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            validateOptions('numberRequest', e.target.checked);
                          }}
                          data-testid='optionNumberRequestView'
                        />
                        <VoipChangeOptionAfterLabel
                          classType={Constants.VoipOptionClassType.VOIP_NUMBER_INFO}
                          servicePackType={changeVoipOptionInfo.optionServicePack}
                          voipOptionPrice={voipOptionPrice}
                          style={{
                            dataTestId: 'numberRequestLabelAfter',
                          }}
                        />
                      </label>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='numberRequestLabelInitial'>
                        {getPrice(voipOptionPrice.voip_options, 'numberRequest', 'initialPrice')}
                      </span>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='numberRequestLabelMonthly'>
                        {getPrice(voipOptionPrice.voip_options, 'numberRequest', 'monthlyPrice')}
                      </span>
                    </td>
                  </tr>
                  {/* キャッチ電話サービス */}
                  <tr>
                    <td className='m-table__td'>
                      <span data-testid='catchPhoneLabel'>キャッチ電話サービス</span>
                    </td>
                    <td className='m-table__td'>
                      <VoipChangeOptionBeforeLabel
                        classType={Constants.VoipOptionClassType.VOIP_CATCH}
                        servicePackType={beforeOptionServicePack}
                        voipOptionPrice={voipOptionPrice}
                        style={{
                          dataTestId: 'catchPhoneLabelBefore',
                        }}
                      />
                    </td>
                    <td className='m-table__td'>
                      <input type='hidden' name='optionCatchPhone' ng-model='optionCatchPhone' />
                      <label htmlFor='op_03'>
                        <input
                          type='checkbox'
                          id='op_03'
                          name='optionCatchPhoneView'
                          checked={changeVoipOptionInfo.optionCatchPhone}
                          disabled={containCatchPhone(changeVoipOptionInfo.optionServicePack)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            validateOptions('catchPhone', e.target.checked);
                          }}
                          data-testid='optionCatchPhoneView'
                        />
                        <VoipChangeOptionAfterLabel
                          classType={Constants.VoipOptionClassType.VOIP_CATCH}
                          servicePackType={changeVoipOptionInfo.optionServicePack}
                          voipOptionPrice={voipOptionPrice}
                          style={{
                            dataTestId: 'catchPhoneLabelAfter',
                          }}
                        />
                      </label>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='catchPhoneLabelInitial'>
                        {getPrice(voipOptionPrice.voip_options, 'catchPhone', 'initialPrice')}
                      </span>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='catchPhoneLabelMonthly'>
                        {getPrice(voipOptionPrice.voip_options, 'catchPhone', 'monthlyPrice')}
                      </span>
                    </td>
                  </tr>
                  {/* 着信お断りサービス */}
                  <tr>
                    <td className='m-table__td'>
                      <span data-testid='stopNuisanceCallLabel'>着信お断りサービス</span>
                    </td>
                    <td className='m-table__td'>
                      <VoipChangeOptionBeforeLabel
                        classType={Constants.VoipOptionClassType.VOIP_BLOCK_CALL}
                        servicePackType={beforeOptionServicePack}
                        voipOptionPrice={voipOptionPrice}
                        style={{
                          dataTestId: 'stopNuisanceCallLabelBefore',
                        }}
                      />
                    </td>
                    <td className='m-table__td'>
                      <input
                        type='hidden'
                        name='optionStopNuisanceCall'
                        ng-model='optionStopNuisanceCall'
                      />
                      <label htmlFor='op_04'>
                        <input
                          type='checkbox'
                          id='op_04'
                          name='optionStopNuisanceCallView'
                          checked={changeVoipOptionInfo.optionStopNuisanceCall}
                          disabled={containStopNuisanceCall(changeVoipOptionInfo.optionServicePack)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            validateOptions('stopNuisanceCall', e.target.checked);
                          }}
                          data-testid='optionStopNuisanceCallView'
                        />
                        <VoipChangeOptionAfterLabel
                          classType={Constants.VoipOptionClassType.VOIP_BLOCK_CALL}
                          servicePackType={changeVoipOptionInfo.optionServicePack}
                          voipOptionPrice={voipOptionPrice}
                          style={{
                            dataTestId: 'stopNuisanceCallLabelAfter',
                          }}
                        />
                      </label>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='stopNuisanceCallLabelInitial'>
                        {getPrice(voipOptionPrice.voip_options, 'stopNuisanceCall', 'initialPrice')}
                      </span>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='stopNuisanceCallLabelMonthly'>
                        {getPrice(voipOptionPrice.voip_options, 'stopNuisanceCall', 'monthlyPrice')}
                      </span>
                    </td>
                  </tr>
                  {/* 着信転送サービス */}
                  <tr>
                    <td className='m-table__td'>
                      <span data-testid='voiceWarpLabel'>着信転送サービス</span>
                    </td>
                    <td className='m-table__td'>
                      <VoipChangeOptionBeforeLabel
                        classType={Constants.VoipOptionClassType.VOIP_TRANSFER}
                        servicePackType={beforeOptionServicePack}
                        voipOptionPrice={voipOptionPrice}
                        style={{
                          dataTestId: 'voiceWarpLabelBefore',
                        }}
                      />
                    </td>
                    <td className='m-table__td'>
                      <input type='hidden' name='optionVoiceWarp' ng-model='optionVoiceWarp' />
                      <label htmlFor='op_05'>
                        <input
                          type='checkbox'
                          id='op_05'
                          name='optionVoiceWarpView'
                          checked={changeVoipOptionInfo.optionVoiceWarp}
                          disabled={containVoiceWarp(changeVoipOptionInfo.optionServicePack)}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                            validateOptions('voiceWarp', e.target.checked);
                          }}
                          data-testid='optionVoiceWarpView'
                        />
                        <VoipChangeOptionAfterLabel
                          classType={Constants.VoipOptionClassType.VOIP_TRANSFER}
                          servicePackType={changeVoipOptionInfo.optionServicePack}
                          voipOptionPrice={voipOptionPrice}
                          style={{
                            dataTestId: 'voiceWarpLabelAfter',
                          }}
                        />
                      </label>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='voiceWarpLabelInitial'>
                        {getPrice(voipOptionPrice.voip_options, 'voiceWarp', 'initialPrice')}
                      </span>
                    </td>
                    <td className='m-table__td amount'>
                      <span data-testid='voiceWarpLabelMonthly'>
                        {getPrice(voipOptionPrice.voip_options, 'voiceWarp', 'monthlyPrice')}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <small>
                <ul>
                  <li>※特に注記のない限り、記載の金額は全て税込金額です。</li>
                  <li>※番号通知リクエストのご利用には、番号表示サービスへのお申込みが必要です。</li>
                  <li>
                    ※着信転送は、ホワイトコール24をご利用予定の場合は、お申込みいただけません。
                  </li>
                </ul>
              </small>
            </div>
            <p
              id='validateOptionsErrorMessage'
              className='m-text'
              data-testid='validateOptionsErrorMessage'
              role={validateOptionsErrorMessage ? 'alert' : undefined}
              aria-live={validateOptionsErrorMessage ? 'assertive' : undefined}
            >
              <strong>{validateOptionsErrorMessage}</strong>
            </p>
            <div className='m-button col2'>
              <BackLinkButton href='/option'>メニューへ戻る</BackLinkButton>
              <Button variant='secondary' type='submit' onClick={clickConfirm}>
                確認する
              </Button>
            </div>
          </div>
        </div>
        <BreadcrumbLists
          breadcrumbs={[
            {
              depth: 1,
            },
          ]}
        />
      </main>
    </>
  );
};

export default VoIPChange;
